"use client";

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import { ArrowRightThin } from "@/components/icons/ArrowRightThin";
import { useConsent } from "@/lib/hooks/useConsent";
import { useAuth } from "@clerk/nextjs";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
});

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.75827 17.2426L12.0009 12M17.2435 6.75736L12.0009 12M12.0009 12L6.75827 6.75736M12.0009 12L17.2435 17.2426"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MuteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="currentColor" />
    <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const UnmuteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5L6 9H2V15H6L11 19V5Z" fill="currentColor" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LightboxContext = createContext(false);
const useLightboxOpen = () => useContext(LightboxContext);

type LightboxItem = {
  src: string;
  alt: string;
  videoId?: string;
};

type LightboxState = {
  isOpen: boolean;
  images: LightboxItem[];
  currentIndex: number;
};

const LightboxProvider = ({ children }: { children: React.ReactNode }) => {
  const [lightbox, setLightbox] = useState<LightboxState>({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [frozenSize, setFrozenSize] = useState<{ width: number; height: number } | null>(null);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const { statistics } = useConsent();
  const { userId } = useAuth();

  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check for video lightbox first
    const videoElement = target.closest("[data-lightbox-video]") as HTMLElement | null;
    if (videoElement) {
      const videoId = videoElement.getAttribute("data-lightbox-video");
      const alt = videoElement.getAttribute("data-lightbox-alt") || "";
      const groupData = videoElement.getAttribute("data-lightbox-group-data");
      const groupIndex = videoElement.getAttribute("data-lightbox-group-index");

      if (videoId) {
        e.preventDefault();
        e.stopPropagation();

        if (groupData) {
          // Video gallery mode
          const items: { videoId: string; alt: string }[] = JSON.parse(groupData);
          setLightbox({
            isOpen: true,
            images: items.map((i) => ({ src: "", alt: i.alt, videoId: i.videoId })),
            currentIndex: Number(groupIndex) || 0,
          });
        } else {
          // Single video
          setLightbox({
            isOpen: true,
            images: [{ src: "", alt, videoId }],
            currentIndex: 0,
          });
        }
      }
      return;
    }

    // Find the closest element with data-lightbox-src (image lightbox)
    const lightboxElement = target.closest("[data-lightbox-src]") as HTMLElement | null;

    if (lightboxElement) {
      const src = lightboxElement.getAttribute("data-lightbox-src");
      const alt = lightboxElement.getAttribute("data-lightbox-alt") || "";
      const group = lightboxElement.getAttribute("data-lightbox-group");

      if (src) {
        e.preventDefault();
        e.stopPropagation();

        if (group) {
          // Gallery mode: collect all images in the same group
          const groupElements = document.querySelectorAll(
            `[data-lightbox-group="${group}"][data-lightbox-src]`
          );
          const images: LightboxItem[] = [];
          let clickedIndex = 0;

          groupElements.forEach((el, index) => {
            const elSrc = el.getAttribute("data-lightbox-src");
            const elAlt = el.getAttribute("data-lightbox-alt") || "";
            if (elSrc) {
              images.push({ src: elSrc, alt: elAlt });
              if (el === lightboxElement) clickedIndex = index;
            }
          });

          setLightbox({
            isOpen: true,
            images,
            currentIndex: clickedIndex,
          });
        } else {
          // Single image mode
          setLightbox({
            isOpen: true,
            images: [{ src, alt }],
            currentIndex: 0,
          });
        }
      }
    }
  }, []);

  const lightboxRef = useRef(lightbox);
  lightboxRef.current = lightbox;

  const freezeAndNavigate = useCallback((direction: "prev" | "next") => {
    const { currentIndex, images } = lightboxRef.current;
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;
    // Bail out if already at the boundary
    if (newIndex < 0 || newIndex >= images.length) return;

    // Capture current content dimensions before transition
    const el = contentRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setFrozenSize({ width: rect.width, height: rect.height });
    }
    setIsTransitioning(true);
    setTimeout(() => {
      setLightbox((prev) => ({ ...prev, currentIndex: newIndex }));
      // Keep frozenSize — it will be cleared by onMediaReady once the new content renders
      setIsTransitioning(false);
    }, 150);
  }, []);

  const onMediaReady = useCallback(() => {
    setFrozenSize(null);
    setMediaLoaded(true);
  }, []);

  const goToPrev = useCallback(() => freezeAndNavigate("prev"), [freezeAndNavigate]);
  const goToNext = useCallback(() => freezeAndNavigate("next"), [freezeAndNavigate]);

  useEffect(() => {
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [handleClick]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox.isOpen || lightbox.images.length <= 1) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightbox.isOpen, lightbox.images.length, goToPrev, goToNext]);

  const closeLightbox = () => {
    setLightbox((prev) => ({ ...prev, isOpen: false }));
    setFrozenSize(null);
    setMediaLoaded(false);
    setIsMuted(false);
    setShouldAutoPlay(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;

    if (deltaX > threshold) {
      goToPrev();
    } else if (deltaX < -threshold) {
      goToNext();
    }

    touchStartX.current = null;
  };

  const currentItem = lightbox.images[lightbox.currentIndex];
  const isGallery = lightbox.images.length > 1;
  const canGoPrev = lightbox.currentIndex > 0;
  const canGoNext = lightbox.currentIndex < lightbox.images.length - 1;
  const isVideo = !!currentItem?.videoId;

  return (
    <LightboxContext.Provider value={lightbox.isOpen}>
      {children}
      <DialogPrimitive.Root open={lightbox.isOpen} onOpenChange={(open) => !open && closeLightbox()}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed top-0 right-0 bottom-0 left-0 z-50"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          />
          <DialogPrimitive.Content
            ref={contentRef}
            className={cn(
              "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
              "mt-6",
              isVideo
                ? "w-[min(360px,90vw)] max-h-[calc(90vh-3rem)]"
                : "w-auto h-auto max-w-[90vw] max-h-[calc(90vh-3rem)]",
              "data-[state=closed]:opacity-0"
            )}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              ...(!isVideo && frozenSize ? { width: frozenSize.width, height: frozenSize.height } : {}),
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <VisuallyHidden>
              <DialogPrimitive.Title>{currentItem?.alt || "Bild"}</DialogPrimitive.Title>
            </VisuallyHidden>

            {/* Media wrapper — fades during navigation */}
            <div className={cn(
              "transition-opacity duration-150",
              isTransitioning ? "opacity-0" : "opacity-100"
            )}>
              {/* Video content */}
              {isVideo && currentItem.videoId && (
                <div className="w-full aspect-[9/16] rounded-lg overflow-hidden" ref={() => onMediaReady()}>
                  <MuxPlayer
                    key={currentItem.videoId}
                    streamType="on-demand"
                    playbackId={currentItem.videoId}
                    autoPlay={shouldAutoPlay}
                    muted={isMuted}
                    defaultHiddenCaptions
                    metadata={{
                      video_title: currentItem.alt,
                      viewer_user_id: userId,
                    }}
                    placeholder={`https://image.mux.com/${currentItem.videoId}/thumbnail.jpg?width=360&height=640&fit_mode=smartcrop`}
                    preload="auto"
                    className="w-full h-full"
                    style={{ "--media-object-fit": "cover", "--media-object-position": "center" } as React.CSSProperties}
                    disableCookies={!statistics}
                    accentColor="#FDFD92"
                    onPause={() => setShouldAutoPlay(false)}
                    onPlay={() => setShouldAutoPlay(true)}
                  />
                </div>
              )}

              {/* Image content */}
              {!isVideo && currentItem?.src && (
                <Image
                  key={currentItem.src}
                  src={currentItem.src}
                  alt={currentItem.alt}
                  width={1920}
                  height={1080}
                  className="max-w-[90vw] max-h-[calc(90vh-3rem)] w-auto h-auto object-contain rounded-lg"
                  style={{ border: "none", outline: "none", color: "transparent" }}
                  onLoad={onMediaReady}
                />
              )}
            </div>

            {mediaLoaded && (
              <>
                <DialogPrimitive.Close
                  className="absolute -top-12 right-0 h-10 w-10 flex items-center justify-center rounded-full bg-sun-100 text-black transition-colors hover:bg-sun-75 focus:outline-none"
                  aria-label="Schliessen"
                >
                  <CloseIcon />
                </DialogPrimitive.Close>
                {isVideo && (
                  <button
                    className="absolute -top-12 left-0 h-10 w-10 flex items-center justify-center rounded-full bg-sun-100 text-black transition-colors hover:bg-sun-75 focus:outline-none"
                    onClick={() => setIsMuted((prev) => !prev)}
                    aria-label={isMuted ? "Ton einschalten" : "Ton ausschalten"}
                  >
                    {isMuted ? <MuteIcon /> : <UnmuteIcon />}
                  </button>
                )}
              </>
            )}

            {/* Gallery navigation — desktop only, positioned next to content, hidden until media loads */}
            {isGallery && canGoPrev && mediaLoaded && (
              <button
                className={cn(
                  "absolute top-1/2 -translate-y-1/2",
                  "hidden md:flex items-center justify-center",
                  "w-10 h-10 rounded-full bg-white shadow-md",
                  "hover:bg-black-100 transition-colors group",
                )}
                style={{ left: "max(-3.5rem, calc(-1 * (50vw - 50% - 1rem)))" }}
                onClick={goToPrev}
                aria-label="Previous"
              >
                <ArrowRightThin className="h-5 w-5 rotate-180 group-hover:text-white transition-colors" />
              </button>
            )}
            {isGallery && canGoNext && mediaLoaded && (
              <button
                className={cn(
                  "absolute top-1/2 -translate-y-1/2",
                  "hidden md:flex items-center justify-center",
                  "w-10 h-10 rounded-full bg-white shadow-md",
                  "hover:bg-black-100 transition-colors group",
                )}
                style={{ right: "max(-3.5rem, calc(-1 * (50vw - 50% - 1rem)))" }}
                onClick={goToNext}
                aria-label="Next"
              >
                <ArrowRightThin className="h-5 w-5 group-hover:text-white transition-colors" />
              </button>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </LightboxContext.Provider>
  );
};

export { LightboxProvider, useLightboxOpen };
